import type { Theme } from '@mui/material/styles';

import {
  HEALTH_SCORE_CONTRIBUTOR_MAP,
  HealthScoreType,
} from '@constants/health-score-info';
import type { Threshold } from '@constants/health-score-info';

export type HealthScoreBand = {
  lower: number;
  upper: number;
  color: string; // actual hex/rgb from theme.palette
};

export function getHealthScoreBands(
  id: string,
  theme: Theme
): HealthScoreBand[] {
  const config = HEALTH_SCORE_CONTRIBUTOR_MAP()[id];
  const thresholds = config.thresholds;
  if (!thresholds.length) return [];

  return thresholds.map((t) => ({
    lower: t.lower,
    upper: t.upper,
    color: theme.palette[t.color].main,
  }));
}

export function getEffectiveHealthScore(id: string, score: number): number {
  if (id === HealthScoreType.Immune) {
    return 100 - (score / 5) * 100;
  }
  if (id === HealthScoreType.Stress) {
    return 100 - score;
  }
  return score;
}

export function findMatchingThreshold(
  effectiveScore: number,
  thresholds: Threshold[]
): Threshold | undefined {
  return thresholds.find((t) => {
    const lowerCheck =
      (t.lowerInclusive ?? true)
        ? effectiveScore >= t.lower
        : effectiveScore > t.lower;

    const upperCheck =
      (t.upperInclusive ?? true)
        ? effectiveScore <= t.upper
        : effectiveScore < t.upper;

    return lowerCheck && upperCheck;
  });
}

/**
 * Returns an MUI palette color string for a health score.
 * - null/undefined -> grey or set an emptyColor
 * - no thresholds configured -> purple (default)
 * - match -> matched threshold color
 */
export function getHealthScoreColor(args: {
  id: string;
  score: number | null | undefined;
  theme: Theme;
  emptyColor?: string;
  defaultColor?: string;
}): string {
  const { id, score, theme } = args;

  const emptyColor = args.emptyColor ?? theme.palette.grey[300];
  const defaultColor = args.defaultColor ?? theme.palette.purple.main;

  if (score === null || score === undefined) return emptyColor;

  const config = HEALTH_SCORE_CONTRIBUTOR_MAP()[id];
  const thresholds = config.thresholds;
  if (thresholds.length === 0) return defaultColor;

  const effective = getEffectiveHealthScore(id, score);
  const matched = findMatchingThreshold(effective, thresholds);

  return matched ? theme.palette[matched.color].main : emptyColor;
}
